"use client";

import {
  DEFAULT_TRAFFIC_SOURCE,
  resolveTrafficSource,
} from "@/components/AdobeDataLayer/utils";
import clientSide from "@/helpers/clientSide";
import { setNewRelicRelease, setNewRelicUserId } from "@/lib/newrelic";
import type { TrafficSource } from "@/components/AdobeDataLayer/utils";
import { usePathname } from "next/navigation";
import { useEffect, useRef } from "react";

const TRAFFIC_SOURCE_ATTRIBUTE = "traffic_source";
const TRAFFIC_SOURCE_STORAGE_KEY = "rv:newrelic:traffic_source";
const SERVED_BY_ATTRIBUTE = "served_by";
const SERVED_BY_HEADER = "x-served-by";
const RELEASE_ATTRIBUTE = "release";
const RELEASE_NAME_ATTRIBUTE = "release_name";

function getStickyTrafficSource(pathname?: string | null): TrafficSource {
  if (!clientSide) {
    return DEFAULT_TRAFFIC_SOURCE;
  }

  try {
    const persistedTrafficSource = sessionStorage.getItem(
      TRAFFIC_SOURCE_STORAGE_KEY
    );
    const trafficSource = resolveTrafficSource(pathname, persistedTrafficSource);

    if (persistedTrafficSource !== trafficSource) {
      sessionStorage.setItem(TRAFFIC_SOURCE_STORAGE_KEY, trafficSource);
    }

    return trafficSource;
  } catch {
    return resolveTrafficSource(pathname);
  }
}

function setCohesionAttributes(
  timeoutMs: number = 5000,
  pollIntervalMs: number = 50
): void {
  const startTime = Date.now();

  const checkCohesion = () => {
    const sessionId = window._Cohesion?.sessionId;
    const anonymousId = window._Cohesion?.anonymousId;
    const hasCohesionIds =
      sessionId && anonymousId && sessionId.trim() && anonymousId.trim();

    if (hasCohesionIds && window.newrelic) {
      window.newrelic.setCustomAttribute("CohesionSessionId", sessionId);
      window.newrelic.setCustomAttribute("CohesionAnonymousId", anonymousId);
      // Identifies the user in Session Replay (otherwise all sessions show as
      // "unknown"). Reusing the Cohesion sessionId keeps replay user IDs
      // aligned with the analytics events emitted via Cohesion/Tagular.
      setNewRelicUserId(sessionId);
      return;
    }

    if (Date.now() - startTime >= timeoutMs) {
      return;
    }

    setTimeout(checkCohesion, pollIntervalMs);
  };

  checkCohesion();
}

async function setServedByAttribute(): Promise<void> {
  // Skip the HEAD probe entirely when the agent never loaded (blocked, CSP,
  // or load-init failure). The attribute wouldn't reach New Relic anyway,
  // so the request would be pure edge waste.
  if (!window.newrelic) return;

  try {
    // Strip query string + hash (use pathname only) so UTM/marketing params
    // don't blow up CDN cache key cardinality for what's effectively the
    // same probe across visits. No explicit cache mode: x-served-by only
    // flips on cutover/rollback, so honoring browser/CDN defaults is fine
    // and avoids per-load edge waste from `cache: "no-store"`.
    const response = await fetch(window.location.pathname, {
      method: "HEAD",
      credentials: "same-origin",
    });
    const servedBy = response.headers.get(SERVED_BY_HEADER);

    if (servedBy && window.newrelic) {
      // Do not persist: cutover attribution must reflect the current
      // response, not a stale value carried over from a prior page load
      // (e.g. SST then rollback within the same session).
      window.newrelic.setCustomAttribute(SERVED_BY_ATTRIBUTE, servedBy, false);
    }
  } catch {
    // Best-effort cutover telemetry only; never let NR tagging affect page load.
  }
}

export default function NewRelicScript() {
  const pathname = usePathname();
  const initialPathnameRef = useRef(pathname);

  useEffect(() => {
    const loadNewRelic = async () => {
      if (
        !clientSide ||
        process.env.NEXT_PUBLIC_NEW_RELIC_ENABLED !== "true"
      ) {
        return;
      }

      // Seed the browser agent before it boots so the landing split is attached
      // to initial page-view events instead of a later client-side route.
      const trafficSource = getStickyTrafficSource(initialPathnameRef.current);

      // Read release metadata up-front so it can be attached as jsAttributes at
      // agent boot — otherwise the initial PageView harvest fires before any
      // post-construction setCustomAttribute call can run.
      const releaseName = process.env.NEXT_PUBLIC_RELEASE_NAME;
      const releaseId = process.env.NEXT_PUBLIC_RELEASE_ID;
      const hasRelease = Boolean(releaseName && releaseId);

      const { BrowserAgent } = await import(
        "@newrelic/browser-agent/loaders/browser-agent"
      );

      const options = {
        init: {
          session_replay: {
            enabled: true,
            block_selector: "",
            mask_text_selector: "",
            sampling_rate: 100.0,
            error_sampling_rate: 100.0,
            // Text input masking config. Per-element overrides: docs/new-relic.md.
            mask_all_inputs: false,
            collect_fonts: true,
            inline_images: false,
            inline_stylesheet: true,
            fix_stylesheets: true,
            preload: false,
            mask_input_options: {
              color: false,
              date: false,
              datetime_local: false,
              email: false,
              month: false,
              number: false,
              range: false,
              search: false,
              tel: false,
              text: true,
              time: false,
              url: false,
              week: false,
              select: false,
              textarea: false,
            },
          },
          distributed_tracing: { enabled: true },
          performance: { capture_marks: true, capture_measures: true },
          browser_consent_mode: { enabled: false },
          privacy: { cookies_enabled: true },
          ajax: {
            deny_list: [
              "bam.nr-data.net",
              "www.mczbf.com",
              "dl.cws.xfinity.com",
              "collector.pabs.comcast.com/xfinityassistant/prod",
            ],
          },
        },
        info: {
          beacon: "bam.nr-data.net",
          errorBeacon: "bam.nr-data.net",
          licenseKey: process.env.NEXT_PUBLIC_NEW_RELIC_LICENSE_KEY!,
          applicationID: process.env.NEXT_PUBLIC_NEW_RELIC_APPLICATION_ID!,
          jsAttributes: {
            [TRAFFIC_SOURCE_ATTRIBUTE]: trafficSource,
            ...(hasRelease && {
              [RELEASE_ATTRIBUTE]: releaseId,
              [RELEASE_NAME_ATTRIBUTE]: releaseName,
            }),
          },
          sa: 1,
        },
        loader_config: {
          accountID: process.env.NEXT_PUBLIC_NEW_RELIC_ACCOUNT_ID,
          trustKey: process.env.NEXT_PUBLIC_NEW_RELIC_TRUST_KEY,
          agentID: process.env.NEXT_PUBLIC_NEW_RELIC_AGENT_ID,
          licenseKey: process.env.NEXT_PUBLIC_NEW_RELIC_LICENSE_KEY,
          applicationID: process.env.NEXT_PUBLIC_NEW_RELIC_APPLICATION_ID,
        },
      };

      new BrowserAgent(options);

      if (window.newrelic) {
        if (hasRelease) {
          // jsAttributes (above) handle initial PageView faceting; addRelease
          // is still required for sourcemap correlation in the New Relic UI.
          setNewRelicRelease(releaseName!, releaseId!);
        }

        window.newrelic.setCustomAttribute(
          TRAFFIC_SOURCE_ATTRIBUTE,
          trafficSource,
          true
        );
      }

      void setServedByAttribute();
      setCohesionAttributes();
    };

    loadNewRelic();
  }, []);

  return null;
}
